/**
 * Toata logica slotului - rulata LOCAL in browser
 * Nu se face niciun API call per spin
 */

/**
 * Calculeaza miza per spin din profilul AI
 */
export function calcMiza(bugetZilnicJoc) {
  const raw = bugetZilnicJoc * 0.003
  return Math.max(2, parseFloat(raw.toFixed(2)))
}

/**
 * Ruleaza un spin si returneaza rezultatul
 * mizaCurenta: 2, 4 sau 8 lei
 */
export function spin(slotConfig, mizaCurenta = 2) {
  const probabilitate = slotConfig?.probabilitate_castig || 0.03
  // Lower overall win chance 
  const probReala = Math.min(probabilitate, 0.03)

  const isWin = Math.random() < probReala
  const multiplicatorMiza = mizaCurenta <= 2 ? 1 : mizaCurenta <= 4 ? 2 : 4

  if (isWin) {
    const { grid, winPayline, matchCount } = getWinSymbols()
    const winSymbol = getPaylineSymbols(grid, PAYLINES[winPayline])[0]
    const castigBaza = getPayout(winSymbol, matchCount)
    const castig = castigBaza * multiplicatorMiza
    return {
      type: 'win',
      simboluri: grid,
      winPayline,
      matchCount,
      suma: castig,
      net: castig - mizaCurenta,
    }
  } else {
    const simboluri = getLoseSymbols()
    return {
      type: 'lose',
      simboluri,
      suma: 0,
      net: -mizaCurenta,
    }
  }
}

/**
 * Alege ce categorie de cheltuiala e afectata de pierdere
 * Proportional cu cheltuielile userului
 */
export function getCategorieAfectata(cheltuieli, context) {
  const categorii = []

  if (cheltuieli.mancare_zilnic > 0) categorii.push({ key: 'mancare', weight: cheltuieli.mancare_zilnic })
  if (cheltuieli.utilitati_zilnic > 0) categorii.push({ key: 'utilitati', weight: cheltuieli.utilitati_zilnic })
  if (cheltuieli.rata_zilnic > 0 && context.are_rata) categorii.push({ key: 'rata', weight: cheltuieli.rata_zilnic * 2 })
  if (cheltuieli.copii_zilnic > 0 && context.are_copii) categorii.push({ key: 'copii', weight: cheltuieli.copii_zilnic * 3 })
  if (cheltuieli.transport_zilnic > 0) categorii.push({ key: 'transport', weight: cheltuieli.transport_zilnic })

  if (!categorii.length) return 'generic'

  const totalWeight = categorii.reduce((s, c) => s + c.weight, 0)
  let r = Math.random() * totalWeight
  for (const cat of categorii) {
    r -= cat.weight
    if (r <= 0) return cat.key
  }
  return 'generic'
}

/**
 * Returneaza mesajul corespunzator categoriei afectate
 */
export function getMesajPierdere(categorie, mesaje, suma) {
  const map = {
    mancare: mesaje.pierdere_mancare,
    utilitati: mesaje.pierdere_utilitati,
    rata: mesaje.pierdere_rata || mesaje.pierdere_generic,
    copii: mesaje.pierdere_copii || mesaje.pierdere_generic,
    transport: mesaje.pierdere_generic,
    generic: mesaje.pierdere_generic,
  }
  return `${map[categorie]} (-${suma} lei)`
}

// --- SIMBOLURI & PAYLINES ---

const TOATE_SIMBOLURI = ['cherries', 'lemon', 'orange', 'plum', 'grapes', 'watermelon', 'seven']

// Payout table: symbol -> { count: payout }
const PAYOUTS = {
  cherries:    { 2: 2, 3: 8, 4: 20, 5: 80 },
  orange:      { 3: 8, 4: 20, 5: 80 },
  lemon:       { 3: 8, 4: 20, 5: 80 },
  plum:        { 3: 8, 4: 20, 5: 80 },
  grapes:      { 3: 20, 4: 80, 5: 200 },
  watermelon:  { 3: 20, 4: 80, 5: 200 },
  seven:       { 3: 40, 4: 400, 5: 2000 },
}

function getPayout(symbol, count) {
  return PAYOUTS[symbol]?.[count] || 0
}

function getMinMatch(symbol) {
  return symbol === 'cherries' ? 2 : 3
}

// Paylines: array of row indices per column [col0, col1, col2, col3, col4]
const PAYLINES = [
  [0, 0, 0, 0, 0], // top row - straight
  [1, 1, 1, 1, 1], // middle row - straight
  [2, 2, 2, 2, 2], // bottom row - straight
  [0, 1, 2, 1, 0], // V-shape (starts top)
  [2, 1, 0, 1, 2], // Λ-shape (starts bottom)
]

function isWinningLine(symbols) {
  let count = 1
  for (let i = 1; i < symbols.length; i++) {
    if (symbols[i] === symbols[i - 1]) count++
    else break
  }
  return getPayout(symbols[0], count) > 0
}

function getPaylineSymbols(grid, payline) {
  return payline.map((row, col) => grid[row][col])
}

function hasAnyWin(grid) {
  return PAYLINES.some(pl => isWinningLine(getPaylineSymbols(grid, pl)))
}

// Max 1 seven per column
function enforceSevenLimit(grid) {
  for (let col = 0; col < 5; col++) {
    let sevenCount = 0
    for (let row = 0; row < 3; row++) {
      if (grid[row][col] === 'seven') {
        sevenCount++
        if (sevenCount > 1) {
          let other
          do { other = TOATE_SIMBOLURI[Math.floor(Math.random() * TOATE_SIMBOLURI.length)] } while (other === 'seven')
          grid[row][col] = other
        }
      }
    }
  }
}

// Weighted symbol selection for wins — rare symbols appear less
const WIN_SYMBOL_WEIGHTS = [
  { symbol: 'cherries', weight: 35 },
  { symbol: 'lemon', weight: 20 },
  { symbol: 'orange', weight: 18 },
  { symbol: 'plum', weight: 14 },
  { symbol: 'grapes', weight: 6 },
  { symbol: 'watermelon', weight: 5 },
  { symbol: 'seven', weight: 2 },
]

function pickWeightedSymbol() {
  const total = WIN_SYMBOL_WEIGHTS.reduce((s, w) => s + w.weight, 0)
  let r = Math.random() * total
  for (const { symbol, weight } of WIN_SYMBOL_WEIGHTS) {
    r -= weight
    if (r <= 0) return symbol
  }
  return 'cherries'
}

// Weighted match count — higher matches are exponentially rarer
function pickMatchCount(minMatch) {
  const r = Math.random()
  if (minMatch === 2) {
    // cherries: 2-match 78%, 3-match 18%, 4-match 3%, 5-match 1%
    if (r < 0.78) return 2
    if (r < 0.96) return 3
    if (r < 0.99) return 4
    return 5
  }
  // others: 3-match 90%, 4-match 8%, 5-match 2%
  if (r < 0.90) return 3
  if (r < 0.98) return 4
  return 5
}

function getWinSymbols() {
  const paylineIdx = Math.floor(Math.random() * PAYLINES.length)
  const payline = PAYLINES[paylineIdx]
  const s = pickWeightedSymbol()
  const minMatch = getMinMatch(s)
  const matchCount = pickMatchCount(minMatch)

  for (let attempt = 0; attempt < 50; attempt++) {
    const grid = [[], [], []]

    // Fill all cells randomly
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 5; col++) {
        grid[row][col] = TOATE_SIMBOLURI[Math.floor(Math.random() * TOATE_SIMBOLURI.length)]
      }
    }

    // Place winning symbols on chosen payline
    for (let col = 0; col < matchCount; col++) {
      grid[payline[col]][col] = s
    }

    // Break the sequence after match
    if (matchCount < 5) {
      const breakRow = payline[matchCount]
      let other
      do { other = TOATE_SIMBOLURI[Math.floor(Math.random() * TOATE_SIMBOLURI.length)] } while (other === s)
      grid[breakRow][matchCount] = other
    }

    // Re-place winning symbols first
    for (let col = 0; col < matchCount; col++) {
      grid[payline[col]][col] = s
    }

    // Enforce max 1 seven per column
    enforceSevenLimit(grid)

    // Re-place winning symbols again (enforceSevenLimit might have overwritten them)
    for (let col = 0; col < matchCount; col++) {
      grid[payline[col]][col] = s
    }

    // If winning symbol is seven, clear extra sevens in those columns
    if (s === 'seven') {
      for (let col = 0; col < matchCount; col++) {
        for (let row = 0; row < 3; row++) {
          if (row !== payline[col] && grid[row][col] === 'seven') {
            let other
            do { other = TOATE_SIMBOLURI[Math.floor(Math.random() * TOATE_SIMBOLURI.length)] } while (other === 'seven')
            grid[row][col] = other
          }
        }
      }
    }

    // Verify only the intended payline wins
    let valid = true
    for (let i = 0; i < PAYLINES.length; i++) {
      if (i === paylineIdx) continue
      if (isWinningLine(getPaylineSymbols(grid, PAYLINES[i]))) {
        valid = false
        break
      }
    }
    if (valid) return { grid, winPayline: paylineIdx, matchCount }
  }

  // Fallback - simple middle row win (payline index 1 = middle straight)
  const fallback = [
    ['plum', 'watermelon', 'lemon', 'orange', 'grapes'],
    [s, s, s, 'plum', 'watermelon'],
    ['orange', 'grapes', 'plum', 'lemon', 'orange'],
  ]
  return { grid: fallback, winPayline: 1, matchCount: 3 }
}

function getLoseSymbols() {
  const nearMiss = Math.random() < 0.45

  for (let attempt = 0; attempt < 100; attempt++) {
    const grid = [[], [], []]

    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 5; col++) {
        grid[row][col] = TOATE_SIMBOLURI[Math.floor(Math.random() * TOATE_SIMBOLURI.length)]
      }
    }

    // Apply near-miss on middle row (2 matching from left, then different)
    if (nearMiss) {
      const candidates = TOATE_SIMBOLURI.filter(s => s !== 'cherries')
      const s = candidates[Math.floor(Math.random() * candidates.length)]
      grid[1][0] = s
      grid[1][1] = s
      let other
      do { other = TOATE_SIMBOLURI[Math.floor(Math.random() * TOATE_SIMBOLURI.length)] } while (other === s)
      grid[1][2] = other
    }

    // Enforce max 1 seven per column
    enforceSevenLimit(grid)

    // Check no win on any payline
    if (!hasAnyWin(grid)) return grid
  }

  // Deterministic safe fallback
  return [
    ['lemon', 'orange', 'plum', 'seven', 'grapes'],
    ['orange', 'plum', 'lemon', 'grapes', 'seven'],
    ['plum', 'seven', 'orange', 'lemon', 'watermelon'],
  ]
}

/**
 * Calculeaza starea bugetului dupa fiecare spin
 */
export function updateBuget(bugetCurent, rezultatSpin) {
  const nou = parseFloat((bugetCurent + rezultatSpin.net).toFixed(2))
  return {
    buget: nou,
    inDatarie: nou < 0,
  }
}

/**
 * Calculeaza procentul din fiecare categorie consumat
 */
export function calcImpactCheltuieli(totalPierdut, cheltuieli) {
  const total = Object.values(cheltuieli).reduce((s, v) => s + v, 0)
  if (total === 0) return {}

  return Object.entries(cheltuieli).reduce((acc, [key, val]) => {
    const proportie = val / total
    acc[key] = {
      suma: val,
      pierdut: parseFloat((totalPierdut * proportie).toFixed(2)),
      procent: parseFloat(((totalPierdut * proportie / val) * 100).toFixed(1)),
    }
    return acc
  }, {})
}
